import { supabase } from '../client'
import type { CloudTaskReminder } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── TASK REMINDERS (Erinnerungen, Plan task-reminders) ─────────────────── */

export async function getCloudTaskReminders(userId: string, since?: Date): Promise<CloudTaskReminder[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('task_reminders')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .order('created_at', { ascending: true })
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get task reminders error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get task reminders error', error)
    return []
  }
}

export async function getCloudTaskReminderIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('task_reminders')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(100000)
    if (error) {
      logSupabaseError('Get task reminder IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get task reminder IDs error', error)
    return []
  }
}

export async function getCloudTaskRemindersByIds(userId: string, ids: string[]): Promise<CloudTaskReminder[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('task_reminders')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get task reminders by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get task reminders by IDs error', error)
    return []
  }
}

/**
 * Weich gelöschte Erinnerungen seit `since`.
 *
 * `updated_at` steht mit Absicht in der Auswahl: `sync-pull` schiebt damit die
 * Wasserlinie weiter. Die älteren Entities lassen es weg, deren Linie bleibt
 * bei einem reinen Lösch-Fenster stehen und der nächste Pull holt es erneut —
 * verschwenderisch, nicht verlustig.
 */
export async function getDeletedCloudTaskReminders(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('task_reminders')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted task reminders error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted task reminders error', error)
    return []
  }
}

export async function createCloudTaskReminder(
  userId: string,
  reminder: Omit<CloudTaskReminder, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudTaskReminder | null> {
  if (!supabase) return null
  try {
    const { data, error } = await supabase
      .from('task_reminders')
      .insert({ ...reminder, user_id: userId })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create task reminder error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create task reminder error', error)
    return null
  }
}

export async function updateCloudTaskReminder(
  reminderId: string,
  updates: Partial<Omit<CloudTaskReminder, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('task_reminders')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', reminderId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update task reminder error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update task reminder error', error)
    return null
  }
}

export async function deleteCloudTaskReminder(reminderId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('task_reminders')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', reminderId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete task reminder error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete task reminder error', error)
    return false
  }
}
